// src/pages/ParentRegister.js
// v4 - polished UI, same behaviour

import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/ParentRegister.css";

const suggestNameFromEmail = (email) => {
  if (!email || !email.includes("@")) return "";
  const local = email.split("@")[0];
  return local
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const ParentRegister = () => {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameDirty, setDisplayNameDirty] = useState(false);
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeLocked, setInviteCodeLocked] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ✅ Extract ?code= from URL on first render
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setInviteCode(code);
      setInviteCodeLocked(true); // Prevent manual editing
    }
  }, []);

  // Auto-suggest display name based on email unless user has typed their own
  useEffect(() => {
    if (!displayNameDirty) {
      setDisplayName(suggestNameFromEmail(email));
    }
  }, [email, displayNameDirty]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    // Guard: ensure we send a non-empty display name
    const finalDisplayName =
      (displayName && displayName.trim()) || suggestNameFromEmail(email) || "";

    try {
      const response = await axios.post("/auth/register-parent", {
        email,
        password,
        display_name: finalDisplayName,
        invite_code: inviteCode,
      });

      localStorage.setItem("token", response.data.access_token);
      setSuccess("Account created! Redirecting…");

      setTimeout(() => {
        // You can change this to '/parent/profile' if you want to route them via the profile page first.
        window.location.href = "/view-photos";
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;

      // 🎯 Friendlier error messages for parents
      let friendlyMessage;

      if (detail === "Invalid or expired invite code") {
        friendlyMessage =
          "This invite link has expired or is no longer valid. " +
          "Please contact your child's school and ask them to send you a new Parent Invite link.";
      } else if (detail === "Email already registered") {
        friendlyMessage =
          "This email address is already registered with GhrianGhraffy. " +
          "Please use the Login page instead, or reset your password if you've forgotten it.";
      } else {
        friendlyMessage =
          detail || "Something went wrong. Please try again in a few minutes.";
      }

      setError(friendlyMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="parent-register-container page">
      <div className="parent-register-card">
        <header className="parent-register-header">
          <h1 className="parent-register-title">Create your Parent Account</h1>
          <p className="parent-register-subtitle">
            Use the invite link from your child&apos;s school to set up secure
            access to their class photos.
          </p>
        </header>

        {error && (
          <p className="error-text" aria-live="polite">
            {error}
          </p>
        )}
        {success && (
          <p className="success-text" aria-live="polite">
            {success}
          </p>
        )}

        <form onSubmit={handleRegister} className="parent-register-form">
          <div className="parent-field-group">
            <label htmlFor="parent-email" className="parent-label">
              Your Email
            </label>
            <input
              id="parent-email"
              type="email"
              className="parent-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-display-name" className="parent-label">
              Display Name
            </label>
            <input
              id="parent-display-name"
              type="text"
              className="parent-input"
              placeholder="What other parents will see"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setDisplayNameDirty(true);
              }}
              required
            />
            <small className="hint-text">
              You can change your display name later from your Profile page.
            </small>
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-password" className="parent-label">
              Create Password
            </label>
            <input
              id="parent-password"
              type="password"
              className="parent-input"
              placeholder="Choose a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-invite-code" className="parent-label">
              Invite Code
            </label>
            <input
              id="parent-invite-code"
              type="text"
              className="parent-input"
              placeholder="Invite code from your school"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              disabled={inviteCodeLocked}
              required
            />
            {inviteCodeLocked && (
              <small className="hint-text">
                This invite code came from your link. If it looks wrong, ask
                your school for a new Parent Invite.
              </small>
            )}
          </div>

          <button
            type="submit"
            className="parent-register-button"
            disabled={submitting}
          >
            {submitting ? "Creating account…" : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ParentRegister;
